import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronUp, FaGithub, FaLinkedin, FaEnvelope } from "react-icons/fa";

import About from "./pages/About";
import Skills from "./pages/Skills";
import Resume from "./pages/Resume";
import Services from "./pages/Services";
import Testimonials from "./pages/Testimonials";
import Stats from "./pages/Stats";
import "./App.css";
import AnimatedNavbar from "./components/AnimatedNavbar";
import Hero3D from "./sections/Hero3D";
import ProjectsParallax from "./sections/ProjectsParallax";
import ContactLottie from "./sections/ContactLottie";
import Sidebar from "./components/Sidebar";

function App() {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
      const sections = [
        "home",
        "about",
        "skills",
        "resume",
        "portfolio",
        "services",
        "testimonials",
        "contact",
      ];
      const current = sections.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div className="App">
      <AnimatedNavbar />
      <Sidebar activeSection={activeSection} />

      {/* Main Content */}
      <main className="pt-20 md:pt-24">
        <section id="home">
          <Hero3D />
        </section>
        <section id="stats">
          <Stats />
        </section>
        <section id="about">
          <About />
        </section>
        <section id="skills">
          <Skills />
        </section>
        <section id="resume">
          <Resume />
        </section>
        <section id="portfolio">
          <ProjectsParallax />
        </section>
        <section id="services">
          <Services />
        </section>
        <section id="testimonials">
          <Testimonials />
        </section>
        <section id="contact">
          <ContactLottie />
        </section>
      </main>

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            onClick={scrollToTop}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="fixed bottom-8 right-8 z-40 w-12 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
          >
            <FaChevronUp size={20} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="bg-gray-100 py-8">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Maneesh Madhusudhanan
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Building the future of decentralized applications with
              cutting-edge blockchain technology and innovative solutions.
            </p>
            <div className="flex justify-center space-x-6 mb-6">
              <a
                href="https://github.com/maneeshmadhusudhanan"
                className="text-gray-400 hover:text-blue-600 transition-colors duration-300"
              >
                GitHub
              </a>
              <a
                href="https://www.linkedin.com/in/maneesh-madhusudhanan-50967a132"
                className="text-gray-400 hover:text-blue-600 transition-colors duration-300"
              >
                LinkedIn
              </a>
              <a
                href="mailto:maneeshroks@gmail.com"
                className="text-gray-400 hover:text-blue-600 transition-colors duration-300"
              >
                Email
              </a>
            </div>
            <div className="border-t border-gray-300 pt-6">
              <p className="text-gray-500">
                © 2024 Maneesh Madhusudhanan. All rights reserved.
              </p>
            </div>
          </motion.div>
        </div>
      </footer>
    </div>
  );
}

export default App;
